'use client';

import { useCallback, useEffect, useState } from 'react';

interface GeolocationPosition {
  latitude: number;
  longitude: number;
}

type GeolocationErrorCode =
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'UNSUPPORTED'
  | 'IP_FALLBACK_FAILED';

interface GeolocationError {
  code: GeolocationErrorCode;
  message: string;
}

export interface IpGeolocationResult {
  city: string;
  country: string;
  countryCode: string;
  latitude: number | null;
  longitude: number | null;
  isApproximate: true;
}

interface UseBrowserGeolocationReturn {
  getCurrentLocation: () => Promise<GeolocationPosition>;
  getIpLocation: () => Promise<IpGeolocationResult>;
  isLoading: boolean;
  error: GeolocationError | null;
  isSupported: boolean;
}

function isGeolocationError(value: unknown): value is GeolocationError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value
  );
}

const BROWSER_ERROR_MAP: Record<number, GeolocationError> = {
  1: {
    code: 'PERMISSION_DENIED',
    message:
      'Location permission denied. Please enable location access in your browser settings.',
  },
  2: {
    code: 'POSITION_UNAVAILABLE',
    message:
      'Location information unavailable. Please check your device settings.',
  },
  3: {
    code: 'TIMEOUT',
    message:
      'Location request timed out. Please try again or enter your city manually.',
  },
};

const FALLBACK_BROWSER_ERROR: GeolocationError = {
  code: 'POSITION_UNAVAILABLE',
  message: 'An error occurred while getting your location.',
};

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false, // Don't need GPS-level accuracy for city detection
  timeout: 10000,
  maximumAge: 300000, // Accept cached position up to 5 minutes old
};

/**
 * Hook for browser geolocation and IP-based location fallback.
 * Provides both precise (GPS) and approximate (IP) location methods.
 */
export function useBrowserGeolocation(): UseBrowserGeolocationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<GeolocationError | null>(null);

  // Initialize as `false` so SSR + first client render agree, then flip
  // to the real value post-mount. Using `useMemo(() => typeof window
  // !== 'undefined' && ...)` produces a different value on the server
  // (false) vs client (true), which is the React 19 hydration mismatch
  // diagnosed in location-autocomplete.tsx — the gated <Button> renders
  // on the client but not the server, breaking hydration.
  const [isSupported, setIsSupported] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- post-mount feature detection (window APIs); required to avoid SSR mismatch
    setIsSupported(typeof window !== 'undefined' && 'geolocation' in navigator);
  }, []);

  const getCurrentLocation =
    useCallback(async (): Promise<GeolocationPosition> => {
      if (!isSupported) {
        const err: GeolocationError = {
          code: 'UNSUPPORTED',
          message: 'Geolocation is not supported by your browser',
        };
        setError(err);
        throw err;
      }

      setIsLoading(true);
      setError(null);

      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setIsLoading(false);
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (geoError) => {
            setIsLoading(false);
            const err =
              BROWSER_ERROR_MAP[geoError.code] ?? FALLBACK_BROWSER_ERROR;
            setError(err);
            reject(err);
          },
          GEOLOCATION_OPTIONS,
        );
      });
    }, [isSupported]);

  /**
   * Get location from IP address using server-side geolocation.
   * Uses Vercel's built-in geo detection (only works on Vercel deployment).
   * Returns city-level accuracy (~10-50km).
   */
  const getIpLocation = useCallback(async (): Promise<IpGeolocationResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/geolocation');

      if (!response.ok) {
        const err: GeolocationError = {
          code: 'IP_FALLBACK_FAILED',
          message:
            response.status === 404
              ? 'Unable to detect location from IP address'
              : 'IP geolocation service unavailable',
        };
        setIsLoading(false);
        setError(err);
        throw err;
      }

      const { city, country, countryCode, latitude, longitude } =
        await response.json();

      setIsLoading(false);

      return {
        city,
        country,
        countryCode,
        latitude,
        longitude,
        isApproximate: true,
      };
    } catch (fetchError) {
      setIsLoading(false);

      if (isGeolocationError(fetchError)) {
        throw fetchError;
      }

      const err: GeolocationError = {
        code: 'IP_FALLBACK_FAILED',
        message: 'Failed to connect to location service',
      };
      setError(err);
      throw err;
    }
  }, []);

  return {
    getCurrentLocation,
    getIpLocation,
    isLoading,
    error,
    isSupported,
  };
}
